import React, { useEffect, useState } from 'react';
import {
  Box,
  Card,
  IconButton,
  Modal,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  Tooltip,
} from '@mui/material';
import {
  Close as CloseIcon,
  DescriptionOutlined as DescriptionIcon,
} from '@mui/icons-material';
import { AxiosError } from 'axios';
import { useSnackbar } from 'notistack';
import { getProgramSectionsByProgramId, putProgramSections } from '../../../requests/api/students';
import handleApiResponse from '../../../utils/handleApiResponse';
import { formatCcy } from '../../../utils/helpers';
import { formatSchedule, formatTeachers } from './utils';
import { AssignSectionModalProps, SectionData } from './types';
import useStyles from './styles';

function AssignSectionModal({
  open,
  onClose,
  studentId,
  programId,
  programFee,
  locationId,
  onSectionAssigned,
  viewMode = false,
}: AssignSectionModalProps) {
  const classes = useStyles();
  const { enqueueSnackbar } = useSnackbar();
  const [sections, setSections] = useState<SectionData[]>([]);
  const [loading, setLoading] = useState<boolean>(false);

  useEffect(() => {
    if (open && programId) {
      fetchSections();
    }
  }, [open, programId, locationId]);

  const fetchSections = async () => {
    setLoading(true);
    try {
      const { sections: sectionsData } = await getProgramSectionsByProgramId(programId, '');
      let filteredSections = sectionsData || [];
      if (locationId) {
        filteredSections = filteredSections.filter(
          (section: SectionData) => section.location?.id === locationId,
        );
      }
      setSections(filteredSections);
    } catch (err) {
      const e = err as AxiosError;
      handleApiResponse(enqueueSnackbar, e, false);
    } finally {
      setLoading(false);
    }
  };

  async function handleAssignSection(sectionId: string): Promise<void> {
    if (!onSectionAssigned || !studentId || !programId) {
      enqueueSnackbar('No se puede asignar sección', { variant: 'error' });
      return;
    }

    try {
      setLoading(true);
      const response = await putProgramSections({
        studentId,
        programId,
        sectionId,
      });
      handleApiResponse(enqueueSnackbar, response, true);
      await onSectionAssigned();
      onClose();
    } catch (err) {
      const e = err as AxiosError;
      handleApiResponse(enqueueSnackbar, e, false);
    } finally {
      setLoading(false);
    }
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
    >
      <Box className={classes.modalContainer}>
        <Card className={classes.modal}>
          <Box className={classes.modalContent}>
            {!viewMode && (
              <Box className={classes.modalTitleContainer}>
                <Box className={classes.modalTitleAndIcon}>
                  <DescriptionIcon className={classes.modalTitleIcon} color="primary" />
                  <Typography className={classes.modalTitle}>
                    Asignar Sección
                  </Typography>
                </Box>
                <IconButton onClick={onClose}>
                  <CloseIcon className={classes.modalTitleIcon} color="primary" />
                </IconButton>
              </Box>
            )}
            {viewMode ? (
              <Box className={classes.modalTitleContainer}>
                <Typography className={classes.sectionsTitle}>
                  Secciones
                </Typography>
                <IconButton onClick={onClose}>
                  <CloseIcon className={classes.modalTitleIcon} color="primary" />
                </IconButton>
              </Box>
            ) : (
              <Typography className={classes.sectionsTitle}>
                Secciones
              </Typography>
            )}
            <TableContainer component={Paper} className={classes.tableContainer}>
              <Table>
                <TableHead>
                  <TableRow className={classes.tableHeader}>
                    <TableCell>Sede</TableCell>
                    <TableCell>Horario</TableCell>
                    <TableCell>Valor</TableCell>
                    <TableCell>Sección</TableCell>
                    <TableCell>Profesor</TableCell>
                    <TableCell>Whatsapp</TableCell>
                    <TableCell>Inscritos / Cupos</TableCell>
                    {!viewMode && <TableCell></TableCell>}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {loading && (
                    <TableRow>
                      <TableCell colSpan={8} align="center">
                        <Typography>Cargando secciones...</Typography>
                      </TableCell>
                    </TableRow>
                  )}
                  {!loading && sections.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={8} align="center">
                        <Typography>No hay secciones disponibles</Typography>
                      </TableCell>
                    </TableRow>
                  )}
                  {!loading && sections.length > 0 && (
                    (() => {
                      const groupedByLocation: Record<string, SectionData[]> = {};
                      sections.forEach((section) => {
                        const locationKey = section.location?.id?.toString() || 'sin-sede';
                        if (!groupedByLocation[locationKey]) {
                          groupedByLocation[locationKey] = [];
                        }
                        groupedByLocation[locationKey].push(section);
                      });

                      const rows: JSX.Element[] = [];
                      Object.entries(groupedByLocation).forEach(
                        ([locationKey, locationSections]) => {
                          const locationName = locationSections[0]?.location?.name || 'Sin sede';
                          const totalStudents = locationSections.reduce(
                            (sum, section) => sum + (Number(section.studentsCount) || 0),
                            0,
                          );

                          const firstSection = locationSections[0];
                          const isFirstSectionFull = Number(firstSection.studentsCount || 0)
                            >= Number(firstSection.studentsCapacity || 0);
                          rows.push(
                            <TableRow
                              key={`location-${locationKey}-first`}
                              className={isFirstSectionFull ? classes.fullRow : undefined}
                            >
                              <TableCell
                                rowSpan={locationSections.length}
                                sx={{
                                  fontWeight: 600,
                                  verticalAlign: 'middle',
                                  textAlign: 'center',
                                  borderRight: '2px solid #e0e0e0',
                                }}
                              >
                                <Typography variant="subtitle1" fontWeight="bold">
                                  {locationName}
                                </Typography>
                                {totalStudents > 0 && (
                                  <Typography variant="caption" display="block">
                                    {totalStudents} alumno{totalStudents > 1 ? 's' : ''}
                                  </Typography>
                                )}
                              </TableCell>
                              <TableCell>{formatSchedule(firstSection.sectionTimeSlots)}</TableCell>
                              <TableCell sx={{ whiteSpace: 'nowrap' }}>
                                {programFee ? formatCcy(programFee, '.').replace('$ ', '$') : '-'}
                              </TableCell>
                              <TableCell>{firstSection.mipreuId || '-'}</TableCell>
                              <TableCell>{formatTeachers(firstSection.sectionTeachers)}</TableCell>
                              <TableCell>-</TableCell>
                              <TableCell>
                                {`${firstSection.studentsCount || 0}/${firstSection.studentsCapacity || 0}`}
                              </TableCell>
                              {!viewMode && (
                                <TableCell>
                                  {isFirstSectionFull ? (
                                    <Tooltip title="Todos los cupos de esta sección están asignados">
                                      <span>
                                        <Button
                                          variant="contained"
                                          color="primary"
                                          size="small"
                                          className={classes.assignButton}
                                          onClick={() => handleAssignSection(
                                            String(firstSection.id),
                                          )}
                                        >
                                          Asignar
                                        </Button>
                                      </span>
                                    </Tooltip>
                                  ) : (
                                    <Button
                                      variant="contained"
                                      color="primary"
                                      size="small"
                                      className={classes.assignButton}
                                      onClick={() => handleAssignSection(String(firstSection.id))}
                                    >
                                      Asignar
                                    </Button>
                                  )}
                                </TableCell>
                              )}
                            </TableRow>,
                          );

                          locationSections.slice(1).forEach((section) => {
                            const isSectionFull = Number(section.studentsCount || 0)
                              >= Number(section.studentsCapacity || 0);
                            rows.push(
                              <TableRow
                                key={section.id}
                                className={isSectionFull ? classes.fullRow : undefined}
                              >
                                <TableCell>{formatSchedule(section.sectionTimeSlots)}</TableCell>
                                <TableCell sx={{ whiteSpace: 'nowrap' }}>
                                  {programFee ? formatCcy(programFee, '.') : '-'}
                                </TableCell>
                                <TableCell>{section.mipreuId || '-'}</TableCell>
                                <TableCell>{formatTeachers(section.sectionTeachers)}</TableCell>
                                <TableCell>-</TableCell>
                                <TableCell>
                                  {`${section.studentsCount || 0}/${section.studentsCapacity || 0}`}
                                </TableCell>
                                {!viewMode && (
                                  <TableCell>
                                    {isSectionFull ? (
                                      <Tooltip title="Todos los cupos de esta sección están asignados">
                                        <span>
                                          <Button
                                            variant="contained"
                                            color="primary"
                                            size="small"
                                            className={classes.assignButton}
                                            onClick={() => handleAssignSection(
                                              String(section.id),
                                            )}
                                          >
                                            Asignar
                                          </Button>
                                        </span>
                                      </Tooltip>
                                    ) : (
                                      <Button
                                        variant="contained"
                                        color="primary"
                                        size="small"
                                        className={classes.assignButton}
                                        onClick={() => handleAssignSection(String(section.id))}
                                      >
                                        Asignar
                                      </Button>
                                    )}
                                  </TableCell>
                                )}
                              </TableRow>,
                            );
                          });
                        },
                      );
                      return rows;
                    })()
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        </Card>
      </Box>
    </Modal>
  );
}

export default AssignSectionModal;
